// Import necessary dependencies
import React, { ChangeEvent } from 'react'
import { useField } from 'formik'
import { Box, FormLabel, HStack, IconButton, Image, VStack, Text } from '@chakra-ui/react'
import { VscTrash } from 'react-icons/vsc'
import { PrimaryButton, SecondaryButton } from 'components/Buttons'

// Define the types for the image object
interface Image {
  id: number
  file: File
}

// Define the Props interface for the component
interface ImageUploadProps {
  name: string
  label: string
}

// ImageUpload component
const ConnectedImagesUploader: React.FC<ImageUploadProps> = ({ name, label }) => {
  // Formik's useField hook
  const [field, meta, helpers] = useField(name)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Function to handle file input change
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event?.target?.files

    if (selectedFiles) {
      const newImages: Image[] = Array.from(selectedFiles).map((file, index) => ({
        id: index,
        file
      }))

      helpers.setValue([...(field?.value || []), ...(newImages || [])])
    }
  }

  // Function to remove an image
  const removeImage = (id: number) => {
    const updatedImages = field.value.filter((image: Image) => image?.id !== id)
    helpers?.setValue(updatedImages)
  }

  return (
    <VStack alignItems="flex-start" spacing={4}>
      <input
        type="file"
        ref={inputRef}
        accept="image/*"
        style={{ visibility: 'hidden', position: 'absolute' }}
        multiple
        onChange={handleFileChange}
        onBlur={field?.onBlur}
      />
      {/* Input for selecting images */}
      {label && (
        <FormLabel fontSize="xs" fontWeight="bold" htmlFor={name}>
          {label}
        </FormLabel>
      )}

      <HStack spacing={4}>
        <PrimaryButton
          type="button"
          minW={40}
          colorScheme="orange"
          onClick={() => inputRef?.current?.click()}
          style={{ marginRight: '10px' }}
        >
          {field?.value?.length > 0 ? 'Upload More' : 'Upload Images'}
        </PrimaryButton>
        {field?.value?.length > 0 && (
          <>
            <SecondaryButton
              type="button"
              color="red"
              colorScheme="red"
              leftIcon={<VscTrash />}
              onClick={() => helpers.setValue([])}
            >
              Remove All
            </SecondaryButton>
            <Text color="gray.500" fontSize="xs">
              {field?.value?.length} images selected
            </Text>
          </>
        )}
      </HStack>

      {/* Display selected images and allow removal */}
      <HStack overflowY="hidden" spacing={5} py={5} width="100%" overflowX="auto">
        {field?.value?.map((image: Image) => (
          <Box position="relative" key={image?.id}>
            <Image
              src={URL.createObjectURL(image?.file)}
              rounded="md"
              alt={`Image-${image?.id}`}
              style={{ maxWidth: '100px', maxHeight: '100px', marginRight: '10px' }}
            />
            <IconButton
              size="sm"
              position="absolute"
              top={0}
              right={0}
              transform="translate(50%,-50%)"
              rounded="full"
              icon={<VscTrash color="red" />}
              aria-label="remove"
              onClick={() => removeImage(image?.id)}
            />
          </Box>
        ))}
      </HStack>

      {/* Display formik error if any */}
      {meta.touched && meta.error && <div style={{ color: 'red' }}>{meta.error}</div>}
    </VStack>
  )
}

export default ConnectedImagesUploader
